import React from 'react';

import { Box, CircularProgress, CircularProgressProps } from '@mui/material';

import styles from './CircularProgressWithLabel.module.scss';

export type CircularProgressWithLabelColorVariant = 'blue' | 'colored';

export interface CircularProgressWithLabelProps extends Omit<CircularProgressProps, 'variant'> {
    /**
     * The value of the progress indicator (0-100)
     */
    value: number;
    /**
     * The size of the circular progress in pixels
     */
    size?: number;
    /**
     * The thickness of the progress circle
     */
    thickness?: number;
    /**
     * Tiered progress stroke palette: blue scale or red–amber–green scale from the theme.
     */
    variant?: CircularProgressWithLabelColorVariant;
    /**
     * When set, used as the progress stroke color at every value instead of tiered theme colors.
     */
    customProgressColor?: string;
}

type ProgressColorTier = 0 | 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100;

function resolveProgressColorTier(progressPercent: number): ProgressColorTier {
    if (progressPercent === 0) return 0;
    if (progressPercent <= 10) return 10;
    if (progressPercent <= 20) return 20;
    if (progressPercent <= 30) return 30;
    if (progressPercent <= 40) return 40;
    if (progressPercent <= 50) return 50;
    if (progressPercent <= 60) return 60;
    if (progressPercent <= 70) return 70;
    if (progressPercent <= 80) return 80;
    if (progressPercent <= 90) return 90;
    return 100;
}

/**
 * Maps progress percentage to the blue tiered stroke token (`--blue-progress-*`).
 */
function getBlueVariantProgressColorCSSValue(progressPercent: number): string {
    const tier = resolveProgressColorTier(progressPercent);
    return `var(--blue-progress-${tier})`;
}

/**
 * Maps progress percentage to the colored tiered stroke token (`--colored-progress-*`).
 */
function getColoredVariantProgressColorCSSValue(progressPercent: number): string {
    const tier = resolveProgressColorTier(progressPercent);
    return `var(--colored-progress-${tier})`;
}

function resolveProgressStrokeColor(
    progressPercent: number,
    colorVariant: CircularProgressWithLabelColorVariant,
    customProgressColor: string | undefined,
): string {
    if (customProgressColor !== undefined) {
        return customProgressColor;
    }
    if (colorVariant === 'colored') {
        return getColoredVariantProgressColorCSSValue(progressPercent);
    }
    return getBlueVariantProgressColorCSSValue(progressPercent);
}

/**
 * Custom Circular Progress with Label component based on MUI CircularProgress
 * Implements design system styling from Figma atoms Activity Complete component
 * Supports light and dark themes via CSS variables
 * Progress color becomes darker as percentage increases (unless overridden)
 */
export const CircularProgressWithLabel: React.FC<CircularProgressWithLabelProps> = ({
    value,
    size = 30,
    thickness = 4,
    className = '',
    variant = 'blue',
    customProgressColor,
    sx,
    ...props
}) => {
    const normalizedValue = Math.min(Math.max(value, 0), 100);

    const progressStrokeColor = resolveProgressStrokeColor(normalizedValue, variant, customProgressColor);

    const progressCircularSx = [...(Array.isArray(sx) ? sx : sx != null ? [sx] : []), { color: progressStrokeColor }];

    return (
        <Box className={`${styles.container} ${className}`} sx={{ width: size, height: size }}>
            <CircularProgress
                variant={'determinate'}
                value={100}
                size={size}
                thickness={thickness}
                className={styles.background}
                {...props}
            />
            <CircularProgress
                variant={'determinate'}
                value={normalizedValue}
                size={size}
                thickness={thickness}
                className={styles.progress}
                sx={progressCircularSx}
                {...props}
            />
            <Box className={styles.label}>{`${Math.round(normalizedValue)}%`}</Box>
        </Box>
    );
};

export default CircularProgressWithLabel;
